import i18n from '@ohif/i18n';
import PdfViewer from '@pdftron/pdfjs-express-viewer';
import { uuidv4 } from '@vindr/utils/helpers';
import cx from '@vindr/utils/helpers/cx';
import { isMobile } from '@vindr/utils/mobile';
import { CSSProperties, forwardRef, useCallback, useEffect, useRef } from 'react';

const contentStyle = {
  width: '100%',
  height: 'calc(100dvh - 45px)',
  margin: 'auto',
};

interface PDFViewerProps {
  url: string | Blob;
  className?: string;
  style?: CSSProperties;
  fileName?: string;
}

const PDFViewer = forwardRef<any, PDFViewerProps>(
  ({ url, className, fileName = uuidv4(), style = {} }, ref) => {
    const pdfViewerWrapper = useRef<HTMLDivElement>(null);
    const currentLanguage = i18n.language;
    const localViewerInstance = useRef<any>(null);
    const viewerInstance = ref || localViewerInstance;
    const hasMobile = isMobile();

    // useLayoutEffect(() => {
    //   // On iOS, we can't open the document in the iframe, so we need to redirect to the document link
    //   if (isIOS()) {
    //     // Redirect to the document link
    //     window.location.href = url;
    //   }
    // }, []);

    const createDoc = useCallback(async () => {
      if (!hasMobile || !url) return;
      if (!viewerInstance.current) return;

      const { UI, Core } = viewerInstance.current;
      // const doc = await Core.createDocument(url, { extension: 'pdf' });
      UI.loadDocument(url, { filename: `${fileName}.pdf` });
    }, [fileName, url]);

    useEffect(() => {
      const element = pdfViewerWrapper.current;
      if (!element || element?.childNodes?.length) return; //? Wait for DOM already & Only init once time
      if (!url) return;
      if (viewerInstance.current) {
        createDoc();
        return;
      }

      (async () => {
        const instance = await PdfViewer(
          {
            path: (window.PUBLIC_URL || '/') + 'webviewer/lib',
            licenseKey: window?.config?.pdfConfig || 'oNtWh5VY9xigblpArWyL',
            css: (window.PUBLIC_URL || '/') + 'assets/pdf-viewer-dark.css',
          },
          pdfViewerWrapper.current
        );
        const { UI } = instance;
        UI.disableElements([
          'contextMenuPopup',
          'languageButton',
          'searchButton',
          'themeChangeButton',
        ]);
        viewerInstance.current = instance;

        const language = currentLanguage === 'vi' ? 'vi' : 'en';
        UI.setLanguage(language);

        createDoc();
      })();

      return () => {
        if (viewerInstance.current) {
          // Dispose or destroy viewer resources
          viewerInstance.current.dispose?.();
          viewerInstance.current = null;
        }
      };
    }, [createDoc, currentLanguage, url]);

    return (
      <div className={className} style={{ ...contentStyle, ...style }}>
        {hasMobile ? (
          <div
            ref={pdfViewerWrapper}
            id={`user-guide`}
            style={{ height: '100%', margin: '0 auto' }}
          />
        ) : (
          <object
            aria-label="HDSD"
            className={cx('h-full w-full', className)}
            data={url}
            style={{ ...contentStyle, ...style }}
            type="application/pdf"
          />
        )}
      </div>
    );
  }
);

PDFViewer.displayName = 'PDFViewer';
export default PDFViewer;
